import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, TouchableOpacity } from 'react-native'
import { Button, Text, Spinner, Radio, Icon, Modal, Card, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import {
  createNewInternalUserWithDualWrite,
  softDeleteUserWithDualWrite,
  enableUserWithDualWrite,
  updateUserEmailWithDualWrite,
  updateUserPasswordWithDualWrite,
  updateUser
} from '../../global/Firebase/firebase'
import HeaderIconButton from '../../components/HeaderIconButton'
import * as Sentry from "@sentry/react-native"
import { HintView } from '../../components/HintView'
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import { useAtomValue } from 'jotai'
import { rolesDictAtom, storesDictAtom, userRoleAtom, usersListAtom } from '../../global/AtomsProvider'
import { useTranslation } from 'react-i18next'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'

export default function UserDetailScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const { detail } = route.params
  const usersList = useAtomValue(usersListAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const rolesDict = useAtomValue(rolesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const postHog = usePostHog()
  const [name, setName] = useState(detail ? detail.name : '')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState(detail ? detail.email : '')
  const [active, setActive] = useState(detail && detail.active ? detail.active : true)
  const [role, setRole] =  useState(detail && detail.role ? detail.role : '')
  // Keep only the slim { id, name } snapshot: docs poisoned by the old store switcher
  // carry the full store object incl. a live native doc.ref (cyclic — crashes
  // JSON.stringify) — saving the slim shape heals them (ATL-555).
  const [store, setStore] =  useState(detail?.store?.id ? { id: detail.store.id, name: detail.store.name } : null)
  const [passwordModalVisible, setPasswordModalVisible] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  // Emit user detail data for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[USER_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        uid: detail.uid,
        name: detail.name,
        email: detail.email,
        role: detail.role,
        // Slim snapshot only — detail.store on poisoned docs contains a live native
        // doc.ref, which is cyclic and makes JSON.stringify throw fatally (ATL-555).
        store: detail.store?.id ? { id: detail.store.id, name: detail.store.name } : null,
        active: detail.active,
        organizationId: context.user?.organizationId,
      })));
    }
  }, [detail]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: detail ? () => (
        <HeaderIconButton
          testID='user-toggle-active'
          onPress={async () => {
            if (detail?.active) {
              // check if admin and that they are not the only admins
              if (detail?.role === 'admin') {
                let admins = usersList.filter(user => user.role === 'admin' && user.active)
                if (admins.length === 1) {
                  alert(t('roles.atLeastOneAdminError', {role: rolesDict?.['admin'].name}))
                  setIsLoading(false)
                  return
                }
              }
              const response = await softDeleteUserWithDualWrite({uid: detail.uid, id:detail.id})
              // ATL-1138: the callable can refuse (no permission, last admin); never report a refusal as done
              alert(response?.data?.status === 0 ? t('roles.disableSuccess') : (response?.data?.error || t('roles.updateUserError')))
            } else {
              const response = await enableUserWithDualWrite({uid: detail.uid, id:detail.id})
              alert(response?.data?.status === 0 ? t('roles.enableSuccess') : (response?.data?.error || t('roles.updateUserError')))
            }
          }}
          iconName={detail?.active ? 'trash-outline' : 'toggle-left-outline'}
        />
      ) : null,
      title: (detail?.name || t('roles.newEmployee'))
    })
  }, [])

  async function save() {
    setIsLoading(true)
    // don't let change the role of admin to something else if there are no more admins
    if (detail?.role === 'admin' && role !== 'admin') {
      // check if there are other admins in the org
      let admins = usersList.filter(user => user.role === 'admin' && user.active)
      if (admins.length === 1) {
        alert(t('roles.atLeastOneAdminError', {role: rolesDict?.['admin'].name}))
        setIsLoading(false)
        return
      }
    }
    if (name !== '' && email !== '' && role !== '' && store !== null && (detail != null || (detail == null && password.length >= 6))) {
      setSaveError('')
      let info = {
        name,
        password,
        email,
        active,
        role, 
        store,
        organizationId: context.user.organizationId
      }
      try {
        if (!detail) {
          let response = await createNewInternalUserWithDualWrite(info)
          if (response?.data?.status == 1) {
            const errorCode = response?.data?.errorCode;
            let errorMessage = response?.data?.error || t('roles.newUserError');
            if (errorCode === 'auth/invalid-email') {
              errorMessage = t('roles.emailInvalidError');
            } else if (errorCode === 'auth/email-already-exists') {
              errorMessage = t('roles.emailAlreadyInUseError');
            } else if (errorCode === 'auth/weak-password') {
              errorMessage = t('roles.passwordWeakError');
            }
            setSaveError(errorMessage);
          } else {
            void sendFBAnalyticsEvent('new_employee')
            postHog.capture('employee_created')
            setSuccessMessageVisible(true)
          }
        } else {
          if (detail.email !== email) {
            if (detail.role === 'admin' && context.user.role !== 'admin') {
              alert(t('roles.onlyAdminCanChangeAdminError'))
            } else {
              let response = await updateUserEmailWithDualWrite({ email, uid: detail.uid })
              if (response?.data?.status == 1) {
                const errorCode = response?.data?.errorCode;
                let errorMessage = t('roles.updateUserError');
                if (errorCode === 'auth/invalid-email') {
                  errorMessage = t('roles.emailInvalidError');
                } else if (errorCode === 'auth/email-already-in-use') {
                  errorMessage = t('roles.emailAlreadyInUseError');
                } else if (errorCode === 'auth/weak-password') {
                  errorMessage = t('roles.passwordWeakError');
                }
                setSaveError(errorMessage);
              } else {
                await updateUser(detail.ref, info)
                setSuccessMessageVisible(true)
              }
            }
          } else {
            await updateUser(detail.ref, info)
            setSuccessMessageVisible(true)
          }
        }
        setIsLoading(false)
      } catch (error) {
        Sentry.captureException(error)
        console.log(error)
        setSaveError(t('error'))
        setIsLoading(false)
      }
    } else {
      setSaveError(t('roles.notAllFieldsFilledOrPasswordShort'))
      setIsLoading(false)
    }
  }

  async function updatePassword() {
    if (detail.role === 'admin' && context.user.role !== 'admin') {
      alert(t('roles.onlyAdminCanChangeAdminError'))
    } else {
      setIsLoading(true)
      let response = await updateUserPasswordWithDualWrite({ password, uid: detail.uid })
      if (response?.data?.status == 1) {
        const errorCode = response?.data?.errorCode;
        let errorMessage = t('roles.updateUserError');
        if (errorCode === 'auth/invalid-email') {
          errorMessage = t('roles.emailInvalidError');
        } else if (errorCode === 'auth/email-already-in-use') {
          errorMessage = t('roles.emailAlreadyInUseError');
        } else if (errorCode === 'auth/weak-password') {
          errorMessage = t('roles.passwordWeakError');
        }
        setSaveError(errorMessage);
      } else {
        setSuccessMessageVisible(true)
        setPasswordModalVisible(false)
      }
      setIsLoading(false)
    }
  }
  
  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center'}}>
          {detail.id + ', ' + detail.uid}
        </Text>}
    
        <Text category='label' style={styles.label}>
          {t('roles.name')}
        </Text>
        <StyledTextInput 
          value={name}
          onChangeText={text => setName(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />

        <Text category='label' style={styles.label}>
          {t('roles.email')}
        </Text>
        <StyledTextInput 
          value={email}
          onChangeText={text => setEmail(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />
        { !detail && (
          <>
            <Text category='label' style={styles.label}>
              {t('roles.password')}
            </Text>
            <StyledTextInput 
              value={password}
              onChangeText={text => setPassword(text)}
              returnKeyType='done'
              autoCapitalize='none'
              autocorrect={false}
            />
            {!detail && <HintView 
              text={t('roles.passwordHint')}
              iconName='alert-circle-outline' style={{marginTop: 10}} 
            />}    
          </>
        )}
        <Text category='label' style={styles.label}>{t('store')}</Text>
        { Object.values(storesDict)?.map((item, index) => (
          <Radio key={item.id} style={styles.radio} checked={store?.id === item.id} onChange={() => {setStore({id: Object.values(storesDict)[index].id, name: Object.values(storesDict)[index].name})}}>
            {item.name}
          </Radio>
        ))}

        <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
          <Text category='label'>
            {t('roles.rolesAndPermissions')}
          </Text>
          { (userRole?.permissions?.['editSuppliers'] || context.user.role === 'admin') && (
            <TouchableOpacity onPress={() => navigation.navigate('UniversalList', {atom: rolesDictAtom, searchField: 'name', detailScreen: 'RoleDetail', title: t('roles.rolesAndPermissions')})} style={{marginLeft: 5, padding: 3}}>
              <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
            </TouchableOpacity>
          )}
        </View>
        { Object.values(rolesDict)?.map((item, index) => (
          <Radio key={item.name} style={styles.radio} checked={role===item.id} onChange={() => {setRole(item.id)}}>
            {item.name}
          </Radio>
        ))} 

        {!detail && <HintView 
          text={t('roles.userLoginHint')} 
          iconName='alert-circle-outline' style={{marginTop: 10}} 
        />}     

        {!detail && <HintView 
          text={t('roles.changePasswordHint')}
          iconName='alert-circle-outline' style={{marginTop: 10}} 
        />}
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}
        <Button appearance='outline' onPress={() => setPasswordModalVisible(true)} style={styles.submitButton}>
          {t('roles.changePassword')}
        </Button>
        <View style={{height: 100}}></View>
        <Modal style={styles.modal} visible={passwordModalVisible} backdropStyle={styles.backdrop} onBackdropPress={() => setPasswordModalVisible(false)}>
          <Card style={styles.modalContainer}>
            <Text category='label' style={styles.label}>
              {t('roles.newPassword')}
            </Text>
            <StyledTextInput 
              value={password}
              onChangeText={text => setPassword(text)}
              returnKeyType='done'
              autoCapitalize='none'
              autocorrect={false}
            />
            {!detail && <HintView 
              text={t('roles.passwordHint')} 
              iconName='alert-circle-outline' style={{marginTop: 10}} 
            />}    
            {saveError != '' && (
              <Text style={styles.messageText} status='danger'>{saveError}</Text>
            )} 
            {isLoading ? (
              <View style={styles.spinner}><Spinner /></View>
            ) : (
              <Button  style={styles.spacedItem} onPress={updatePassword}>
                {t('save')}
              </Button>
            )}
            <Button status='basic' style={styles.spacedItem} onPress={() => setPasswordModalVisible(false)}>
              {t('cancel')}
            </Button>
          </Card>
        </Modal>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  radio: {
    marginBottom: 5
  },
  modal: {
    top: 100,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'space-around',
    alignItems: 'stretch',
    width: 300,
    height: '80%',
    padding: 16,
    margin: 32
  },
  spacedItem: {
    marginVertical: 4,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
});